import { fetchProductById } from './productService';
import { fetchCollectionById } from './collectionService';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1706';


// Helper function to create URL slug from text
export const createSlug = (text) => {
  if (!text) return '';

  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')          // Remove special characters
    .replace(/\s+/g, '-')             // Replace spaces with -
    .replace(/-+/g, '-')              // Replace multiple - with single -
    .replace(/^-+|-+$/g, '');          // Remove leading/trailing -
};

// Helper function to parse URL and extract collection and product names
export const parseProductUrl = (urlPath) => {
  if (!urlPath) return null;

  // Remove leading/trailing slashes
  const cleanPath = urlPath.replace(/^\/+|\/+$/g, '');

  // Split by hyphens to get parts
  const parts = cleanPath.split('-');

  // We need at least 2 parts (collection and product)
  if (parts.length < 2) return null;

  // Find the last part that could be a product name (after collection name)
  // This is a simplified approach - in a real app you might have more sophisticated parsing

  // Try to find the product name by looking for known collection names
  // For now, we'll assume the last part is the product name and everything before is collection
  const productName = parts[parts.length - 1];
  const collectionName = parts.slice(0, parts.length - 1).join('-');

  return {
    collectionName,
    productName,
    fullPath: cleanPath
  };
};

// Function to find product by collection and product names
export const findProductByNames = async (collectionName, productName) => {
  try {
    // First, get all collections to find the matching collection
    const collectionsResponse = await fetch(`${API_URL}/collections`);
    const collectionsData = await collectionsResponse.json();

    if (!collectionsData || !Array.isArray(collectionsData)) {
      console.error('Invalid collections data received');
      return null;
    }

    // Find collection that matches the name (case insensitive)
    const matchingCollection = collectionsData.find(collection =>
      createSlug(collection.name) === collectionName
    );

    if (!matchingCollection) {
      console.log(`Collection not found: ${collectionName}`);
      return null;
    }

    // Now find products in this collection
    const productsResponse = await fetch(`${API_URL}/products/collection/${matchingCollection._id}`);
    const productsData = await productsResponse.json();

    if (!productsData || !Array.isArray(productsData)) {
      console.error('Invalid products data received');
      return null;
    }

    // Find product that matches the name (case insensitive)
    const matchingProduct = productsData.find(product =>
      createSlug(product.name) === productName
    );

    if (!matchingProduct) {
      console.log(`Product not found in collection: ${productName}`);
      return null;
    }

    return {
      collection: matchingCollection,
      product: matchingProduct
    };

  } catch (error) {
    console.error('Error finding product by names:', error);
    return null;
  }
};

// Function to generate product URL from collection and product
export const generateProductUrl = (collection, product) => {
  if (!collection || !product) return '/shop-details';

  const collectionSlug = createSlug(collection.name);
  const productSlug = createSlug(product.name);

  return `/${collectionSlug}-${productSlug}`;
};

// Function to get product details for the new routing format
export const getProductDetailsForUrl = async (urlPath) => {
  const parsed = parseProductUrl(urlPath);
  if (!parsed) return null;

  return await findProductByNames(parsed.collectionName, parsed.productName);
};

// Function to generate product URL from product data
export const generateProductUrlFromData = (product, collection) => {
  if (!product) return '/shop-details';

  // If product has a productId, use it directly for the new routing format
  if (product.productId) {
    return `/shop/${product.productId}`;
  }

  // Fallback to old format for products without productId
  const collectionSlug = collection ? createSlug(collection.name) : 'unknown-collection';
  const productSlug = createSlug(product.name);

  return `/shop/${collectionSlug}-${productSlug}`;
};